import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaSearch, FaPlus, FaEye, FaEdit, FaTrash, FaChevronLeft, FaChevronRight, FaFileAlt, FaUsers, FaTimes, FaEllipsisV } from 'react-icons/fa';
import Select from 'react-select';
import useFetch from '../constants/useFetch';
import { API_BASE_URL } from '../constants';
import { useSelector } from 'react-redux';

const Classes = () => {
  const navigate = useNavigate();
  const { user: userInfo } = useSelector((state) => state.auth);
  const { data: dashboardData, loading: dashboardLoading, error: dashboardError } = useFetch(`${API_BASE_URL}/api/web/admin/all-classes`, 'allClasses');
  const classes = dashboardData?.classrooms || [];
  const subjects = dashboardData?.subjects || [];
  // console.log({classes});
  
  // Mock user role from Redux
  const userRole = userInfo?.role || 'staff';
  const isAdmin = userRole === 'admin';
  const isStaff = userRole === 'staff';

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [classToDelete, setClassToDelete] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isSubjectModalOpen, setIsSubjectModalOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [showAssignSubjectsModal, setShowAssignSubjectsModal] = useState(false);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [assigning, setAssigning] = useState(false);

  const filteredClasses = classes.filter((cls) => {
    const matchesSearch =
      cls.class_name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      cls.form_teacher_title?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      cls.form_teacher_firstname?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      cls.form_teacher_lastname?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      (cls.class_prefect_name || '').toLowerCase().includes(searchTerm?.toLowerCase()) ||
      (cls.asst_class_prefect_name || '').toLowerCase().includes(searchTerm?.toLowerCase());
    const matchesFilter = !filterValue || cls.category?.includes(filterValue);
    return matchesSearch && matchesFilter;
  });

  const totalPages = Math.ceil(filteredClasses.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentClasses = filteredClasses.slice(startIndex, endIndex);

  const subjectOptions = subjects.map((subject) => ({
    value: subject.subject_name,
    label: subject.subject_name,
  }));

  // Get current session and term from dashboardData
  const currentSession = dashboardData?.currentSession || '';
  const currentTerm = dashboardData?.currentTerm || '';

  // Checkbox logic
  const allOnPageSelected = currentClasses.length > 0 && currentClasses.every(cls => selectedClassIds.includes(cls.id));
  const toggleSelectAll = () => {
    if (allOnPageSelected) {
      setSelectedClassIds(prev => prev.filter(id => !currentClasses.some(cls => cls.id === id)));
    } else {
      setSelectedClassIds(prev => [
        ...prev.filter(id => !currentClasses.some(cls => cls.id === id)),
        ...currentClasses.map(cls => cls.id),
      ]);
    }
  };

  const toggleClass = (id) => {
    setSelectedClassIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Handle assign subjects
  const handleAssignSubjects = async () => {
    if (selectedClassIds.length === 0) {
      alert('Please select at least one class');
      return;
    }

    if (selectedSubjects.length === 0) {
      alert('Please select at least one subject');
      return;
    }

    setAssigning(true);
    try {
      const subjectIds = selectedSubjects.map(sub => {
        const subject = subjects.find(s => s.subject_name === sub);
        return subject?.id;
      }).filter(Boolean);

      const res = await fetch(`${API_BASE_URL}/api/web/admin/assign-class-subjects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({
          classroom_ids: selectedClassIds,
          subject_ids: subjectIds
        })
      });

      const json = await res.json();
      if (json.success) {
        alert('Subjects assigned successfully!');
        setShowAssignSubjectsModal(false);
        setSelectedClassIds([]);
        setSelectedSubjects([]);
        // Refresh the page to show updated data
        window.location.reload();
      } else {
        alert(json.message || 'Failed to assign subjects');
      }
    } catch (error) {
      console.error('Error assigning subjects:', error);
      alert('Error assigning subjects');
    } finally {
      setAssigning(false);
    }
  };

  // Get assigned subjects for a single class
  const getAssignedSubjectsForClass = (classId) => {
    const cls = classes.find(c => c.id === classId);
    if (!cls || !cls.offered_subject_ids || !Array.isArray(cls.offered_subject_ids)) return [];
    return cls.offered_subject_ids.map(subId => {
      const subject = subjects.find(s => s.id === subId);
      return subject?.subject_name;
    }).filter(Boolean);
  };

  const handleDelete = async () => {
    if (!classToDelete) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/web/admin/delete-class/${classToDelete}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
      });
      const result = await res.json();
      if (result.success) {
        alert('Class deleted successfully!');
        window.location.reload(); // Replace with refetch if available
      } else {
        alert(result.message || 'Failed to delete class.');
      }
    } catch (err) {
      console.error('Error deleting class:', err);
      alert('Error deleting class. Please try again.');
    } finally {
      setIsDeleteModalOpen(false);
      setClassToDelete(null);
    }
  };

  const openDeleteModal = (className) => {
    setClassToDelete(className);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setClassToDelete(null);
  };

  const openSubjectModal = (className) => {
    setSelectedClass(className);
    setIsSubjectModalOpen(true);
    setOpenDropdown(null);
  };

  const closeSubjectModal = () => {
    setIsSubjectModalOpen(false);
    setSelectedClass(null);
    setSelectedSubject(null);
  };

  const handleSubjectSubmit = () => {
    if (!selectedSubject) {
      alert('Please select a subject.');
      return;
    }
    navigate(`/results?classOption=${encodeURIComponent(selectedClass)}&subjectOption=${encodeURIComponent(selectedSubject.value)}`);
    closeSubjectModal();
  };

  const toggleDropdown = (index) => {
    setOpenDropdown(openDropdown === index ? null : index);
  };

  const handleEditClass = (classId) => {
    console.log({classId});
    
    setOpenDropdown(null);
    navigate(`/edit-class/${classId}`);
  };

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '42px',
      borderColor: '#d1d5db',
      backgroundColor: '#fff',
      '&:hover': { borderColor: '#3b82f6' },
      '[data-theme="dark"] &': {
        backgroundColor: '#1f2937',
        borderColor: '#4b5563',
        color: '#fff',
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#374151',
      '[data-theme="dark"] &': {
        color: '#fff',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#3b82f6' : '#fff',
      color: state.isSelected ? '#fff' : '#374151',
      '&:hover': {
        backgroundColor: state.isSelected ? '#3b82f6' : '#f3f4f6',
      },
      '[data-theme="dark"] &': {
        backgroundColor: state.isSelected ? '#3b82f6' : '#1f2937',
        color: state.isSelected ? '#fff' : '#d1d5db',
        '&:hover': {
          backgroundColor: state.isSelected ? '#3b82f6' : '#374151',
        },
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#fff',
      '[data-theme="dark"] &': {
        backgroundColor: '#1f2937',
      },
    }),
    input: (provided) => ({
      ...provided,
      color: '#374151',
      '[data-theme="dark"] &': {
        color: '#fff',
      },
    }),
  };

  if (dashboardLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100 dark:bg-gray-900">
        <div className="w-12 h-12 border-4 border-blue-500 border-dashed rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 bg-gray-100 dark:bg-gray-900 min-h-screen">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Classes</h1>
            <p className="text-blue-100">Manage all classes in the school</p>
          </div>
          {isAdmin ? (
            <Link
              to="/add-class"
              className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-lg transition-colors"
            >
              <FaPlus className="w-5 h-5" />
              <span>Create New Class</span>
            </Link>
          ) : (
            <div className="flex items-center space-x-2 bg-white/20 text-white px-6 py-3 rounded-lg">
              <span className="text-lg font-bold">{classes.length}</span>
              <span>Class{classes.length !== 1 ? 'es' : ''}</span>
            </div>
          )}
        </div>
      </div>

      {/* Error Display */}
      {dashboardError && (
        <div className="text-red-600 text-center mb-4">{dashboardError}</div>
      )}

      {/* Bulk Actions */}
      {selectedClassIds.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border p-4">
          <p className="font-medium mb-3">{selectedClassIds.length} class{selectedClassIds.length > 1 ? 'es' : ''} selected</p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowAssignSubjectsModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <FaFileAlt />
              Assign Subjects
            </button>
            <button
              onClick={() => setSelectedClassIds([])}
              className="flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Search and Filter Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex-1 min-w-[300px] relative">
            <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-300" />
            <input
              type="text"
              placeholder="Search classes, teachers, or prefects..."
              className="w-full pl-12 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
          >
            <option value="">All Classes</option>
            <option value="junior">Junior Secondary</option>
            <option value="senior">Senior Secondary</option>
          </select>
          <div className="flex items-center gap-2">
            <span className="text-gray-700 dark:text-gray-300">Show:</span>
            <select
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={toggleSelectAll}
                    className="rounded"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Class Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Form Teacher</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">No. of Students</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Class Prefect</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Assistant Class Prefect</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {currentClasses.map((cls, index) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedClassIds.includes(cls.id)}
                      onChange={() => toggleClass(cls.id)}
                      className="rounded"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">{cls.class_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">
                    {cls.form_teacher_title} {cls.form_teacher_firstname} {cls.form_teacher_lastname}
                    <p>{cls.form_teacher_phone && ` (${cls.form_teacher_phone})`}</p>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">{cls.student_count}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">
                    {cls.captain_firstname ? (
                      <>
                        <div>{`${cls.captain_firstname} ${cls.captain_lastname}`}</div>
                        <div className="text-sm text-gray-500">{cls.captain_middlename || 'N/A'}</div>
                      </>
                    ) : (
                      'N/A'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">
                    {cls.assistant_firstname ? (
                      <>
                        <div>{`${cls.assistant_firstname} ${cls.assistant_lastname}`}</div>
                        <div className="text-sm text-gray-500">{cls.assistant_middlename || 'N/A'}</div>
                      </>
                    ) : (
                      'N/A'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap relative">
                    <button
                      onClick={() => toggleDropdown(index)}
                      className="flex items-center justify-center w-8 h-8 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      <FaEllipsisV />
                    </button>
                    {openDropdown === index && (
                      <div className="absolute right-10 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-10">
                        <div className="py-1">
                          {isAdmin ? (
                            <>
                              <Link
                                to={`/classes/${cls.class_name}`}
                                className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                onClick={() => setOpenDropdown(null)}
                              >
                                <FaEye />
                                View Class
                              </Link>

                              {/* <Link
                                to={`/edit-class/${cls.uuid}`}
                                className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                onClick={() => setOpenDropdown(null)}
                              >
                                <FaEdit />
                                Edit Class
                              </Link> */}

                              <button 
                                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" 
                                onClick={() => handleEditClass(cls.uuid)}
                              >
                                <FaEdit className="w-4 h-4 mr-2" /> Edit Class
                              </button>

                              <Link
                                // to={`/all-students-by-class?class=${encodeURIComponent(cls.class_name)}`}
                                to={`/students?classId=${cls.uuid}`}
                                // onClick={() => navigate(`/students?classId=${cls.uuid}`)}
                                className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                onClick={() => setOpenDropdown(null)}
                              >
                                <FaUsers />
                                View Students
                              </Link>

                              {/* <Link
                                to={`/students/create?class=${encodeURIComponent(cls.class_name)}`}
                                className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                onClick={() => setOpenDropdown(null)}
                              >
                                <FaPlus />
                                Add New Student
                              </Link> */}

                              <button
                                onClick={() => openSubjectModal(cls.class_name)}
                                className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left"
                              >
                                <FaFileAlt />
                                Enter Results
                              </button>
                              
                              <button
                                onClick={() => {
                                  openDeleteModal(cls.class_name);
                                  setOpenDropdown(null);
                                }}
                                className="flex items-center gap-2 px-4 py-2 text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left"
                              >
                                <FaTrash />
                                Delete Class
                              </button>
                            </>
                          ) : isStaff ? (
                            <>
                              <Link
                                to={`/classes/${cls.class_name}`}
                                className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                onClick={() => setOpenDropdown(null)}
                              >
                                <FaEye />
                                View Class
                              </Link>
                              <Link
                                to={`/all-students-by-class?class=${encodeURIComponent(cls.class_name)}`}
                                className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                onClick={() => setOpenDropdown(null)}
                              >
                                <FaUsers />
                                View Students
                              </Link>
                              <Link
                                to={`/results?classOption=${cls.class_name}`}
                                className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                onClick={() => setOpenDropdown(null)}
                              >
                                <FaFileAlt />
                                View Results
                              </Link>
                            </>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {currentClasses.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    No classes found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-center mt-6">
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="w-10 h-10 flex items-center justify-center bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaChevronLeft className="text-gray-700 dark:text-gray-300" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-10 h-10 flex items-center justify-center rounded-md ${
                  currentPage === page
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="w-10 h-10 flex items-center justify-center bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaChevronRight className="text-gray-700 dark:text-gray-300" />
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Confirm Delete</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Are you sure you want to delete the class <span className="font-semibold">{classToDelete}</span>?
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={closeDeleteModal}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <FaTimes />
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <FaTrash />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subject Selection Modal */}
      {isSubjectModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={closeSubjectModal}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Select Subject</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Choose a subject to enter results for <span className="font-semibold">{selectedClass}</span>.
            </p>
            <div className="space-y-4">
              <Select
                options={subjectOptions}
                value={selectedSubject}
                onChange={setSelectedSubject}
                placeholder="Select a subject"
                isSearchable
                styles={customSelectStyles}
                className="react-select-container"
                classNamePrefix="react-select"
              />
              <div className="flex justify-end gap-4">
                <button
                  onClick={closeSubjectModal}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <FaTimes />
                  Close
                </button>
                <button
                  onClick={handleSubjectSubmit}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <FaFileAlt />
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Subjects Modal */}
      {showAssignSubjectsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Assign Subjects to {selectedClassIds.length} Class{selectedClassIds.length > 1 ? 'es' : ''}
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              {selectedClassIds.length === 1 
                ? `Select subjects for ${classes.find(c => c.id === selectedClassIds[0])?.class_name || 'selected class'}. Currently assigned subjects are highlighted.`
                : 'Select subjects to assign to all selected classes. You can deselect subjects to remove them.'}
            </p>

            <div className="space-y-4 mb-6">
              <div className="flex flex-wrap gap-2">
                {subjects.map((subject) => {
                  const isSelected = selectedSubjects.includes(subject.subject_name);
                  const isAssigned = selectedClassIds.length === 1 
                    ? getAssignedSubjectsForClass(selectedClassIds[0]).includes(subject.subject_name)
                    : false;
                  
                  return (
                    <button
                      key={subject.id}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedSubjects(prev => prev.filter(s => s !== subject.subject_name));
                        } else {
                          setSelectedSubjects(prev => [...prev, subject.subject_name]);
                        }
                      }}
                      className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                        isSelected
                          ? 'bg-blue-600 text-white border-blue-600'
                          : isAssigned
                          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 border-green-300 dark:border-green-700'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      {subject.subject_name}
                      {isAssigned && !isSelected && (
                        <span className="ml-2 text-xs">(assigned)</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end gap-4">
              <button
                onClick={() => {
                  setShowAssignSubjectsModal(false);
                  setSelectedSubjects([]);
                }}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                disabled={assigning}
              >
                <FaTimes />
                Cancel
              </button>
              <button
                onClick={handleAssignSubjects}
                disabled={assigning || selectedSubjects.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {assigning ? 'Assigning...' : 'Assign Subjects'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Classes;